// Settings UI for connecting a user's own local AI browser agent (e.g. OpenClaw).
// Lets the user mint/list/revoke long-lived API keys and walks them through
// installing and running the reference connector. The agent then fulfills
// "Auto-import via agent" jobs triggered from the Accounts page.

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  SmartToy as SmartToyIcon,
} from "@mui/icons-material";
import {
  agentService,
  AgentApiKey,
} from "../../services/external/agentService";

const CONNECTOR_DOC =
  "https://github.com/s3w47m88/focus-finance/tree/production/agent-connector";

const LocalAgentSettings: React.FC = () => {
  const [keys, setKeys] = useState<AgentApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [minting, setMinting] = useState(false);
  const [newKeyName, setNewKeyName] = useState("Local agent");
  const [mintedPlaintext, setMintedPlaintext] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setKeys(await agentService.listKeys());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load keys");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleMint = async () => {
    setMinting(true);
    setError(null);
    try {
      const minted = await agentService.mintKey(
        newKeyName.trim() || "Local agent"
      );
      setMintedPlaintext(minted.key);
      await loadKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create key");
    } finally {
      setMinting(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      await agentService.revokeKey(id);
      await loadKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to revoke key");
    }
  };

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text);
  };

  const activeKeys = keys.filter((k) => !k.revoked_at);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Alert severity="info" icon={<SmartToyIcon />}>
        Connect your own local AI browser agent (like OpenClaw) to auto-import
        transactions from sources Plaid can&apos;t reach — Cash App first. The
        agent runs on your computer with your logins; your credentials never
        reach our servers.
      </Alert>

      {error && <Alert severity="error">{error}</Alert>}

      {/* Newly minted key — shown once */}
      {mintedPlaintext && (
        <Alert
          severity="success"
          onClose={() => setMintedPlaintext(null)}
          sx={{ wordBreak: "break-all" }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Copy your key now — it won&apos;t be shown again:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <code style={{ flex: 1 }}>{mintedPlaintext}</code>
            <Tooltip title="Copy">
              <IconButton size="small" onClick={() => copy(mintedPlaintext)}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Alert>
      )}

      {/* Mint a new key */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          size="small"
          label="Key name"
          value={newKeyName}
          onChange={(e) => setNewKeyName(e.target.value)}
        />
        <Button
          variant="contained"
          startIcon={minting ? <CircularProgress size={16} /> : <AddIcon />}
          disabled={minting}
          onClick={handleMint}
        >
          Create key
        </Button>
      </Box>

      {/* Existing keys */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Your agent keys
        </Typography>
        {loading ? (
          <CircularProgress size={20} />
        ) : activeKeys.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No active keys yet.
          </Typography>
        ) : (
          <List dense>
            {activeKeys.map((k) => (
              <ListItem
                key={k.id}
                secondaryAction={
                  <Tooltip title="Revoke">
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => handleRevoke(k.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      {k.name}
                      <Chip size="small" label={`${k.key_prefix}…`} />
                    </Box>
                  }
                  secondary={
                    k.last_used_at
                      ? `Last used ${new Date(k.last_used_at).toLocaleString()}`
                      : "Never used"
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      <Divider />

      {/* Setup guide */}
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Set up the connector
        </Typography>
        <Stepper orientation="vertical" nonLinear activeStep={-1}>
          <Step expanded>
            <StepLabel>Create a key (above)</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary">
                Name it for the machine that will run the agent and copy it.
              </Typography>
            </StepContent>
          </Step>
          <Step expanded>
            <StepLabel>Install the connector</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary">
                Requires Node 18+. Download the reference connector from{" "}
                <a href={CONNECTOR_DOC} target="_blank" rel="noreferrer">
                  the agent-connector folder
                </a>
                .
              </Typography>
            </StepContent>
          </Step>
          <Step expanded>
            <StepLabel>Run it with your key</StepLabel>
            <StepContent>
              <Box
                component="pre"
                sx={{
                  p: 1.5,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  overflowX: "auto",
                  fontSize: 13,
                }}
              >
                FF_AGENT_KEY=ff_agent_xxxxx node connector.mjs
              </Box>
              <Typography variant="body2" color="text.secondary">
                Runs in safe DRY-RUN mode until you set{" "}
                <code>FF_AGENT_CMD</code> to launch your real browser agent
                (e.g. OpenClaw).
              </Typography>
            </StepContent>
          </Step>
          <Step expanded>
            <StepLabel>Trigger an import</StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary">
                On the Accounts page, open a Cash App account and click the{" "}
                <SmartToyIcon fontSize="inherit" /> Auto-import button. The
                connector will pick up the job and import your transactions.
              </Typography>
            </StepContent>
          </Step>
        </Stepper>
      </Box>
    </Box>
  );
};

export default LocalAgentSettings;
